<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="editor-body">
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <div class="row">
        <div class="col-xs-12 leftcol">
          <h3>{{ _('General') }}</h3>
          <div class="form-group">
            <label for="paymentPlanName">{{ _('Name') }}</label>
            <input type="text" class="form-control" id="paymentPlanName" v-model="paymentPlan.name" :placeholder="_('Enter payment plan name')" />
          </div>

          <div class="form-group">
            <label>{{ _('Plan type') }}</label>
            <div class="radio" style="display: grid; grid-template-columns: auto auto; gap: 10px;">
              <label>
                <input type="radio" v-model="paymentPlan.type" value="advance" /> {{ _('Advance Payment / Final Payment') }}
              </label>
              <label>
                <input type="radio" v-model="paymentPlan.type" value="recurring" /> {{ _('Recurring Payment') }}
              </label>
            </div>
          </div>

          <div v-if="paymentPlan.type === 'advance'">
            <div class="form-group">
              <label>{{ _('Initial payment amount type') }}</label>
              <select class="form-control" v-model="paymentPlan.amount_type">
                <option value="relative">{{ _('Relative (percentage of total)') }}</option>
                <option value="fixed">{{ _('Fixed amount') }}</option>
              </select>
            </div>

            <div class="form-group" :class="{ 'has-error': amountError }">
              <label for="paymentPlanAmount">{{ _('Initial payment amount') }}</label>
              <div class="input-group">
                <input
                  type="number"
                  data-android-inputmode="decimal"
                  step="any"
                  min="0.01"
                  :max="paymentPlan.amount_type === 'relative' ? '100' : undefined"
                  class="form-control"
                  id="paymentPlanAmount"
                  v-model.number="paymentPlan.amount"
                  :placeholder="paymentPlan.amount_type === 'relative' ? _('Enter percentage') : _('Enter amount')"
                />
                <span class="input-group-addon">{{ paymentPlan.amount_type === 'relative' ? '%' : settings.currency }}</span>
              </div>
              <p v-if="amountError" class="help-block">{{ amountError }}</p>
              <p v-else-if="paymentPlan.amount_type === 'relative'" class="help-block">
                {{ _('Remaining') }}: {{ remainingPercentage }}%
              </p>
            </div>

            <div class="form-group" v-if="paymentPlan.amount_type === 'relative'" :class="{ 'has-error': minimumAmountError }">
              <label for="paymentPlanMinimumAmount">{{ _('Minimum amount') }} ({{ settings.currency }})</label>
              <input
                type="number"
                data-android-inputmode="decimal"
                step="any"
                min="0.01"
                class="form-control"
                id="paymentPlanMinimumAmount"
                v-model.number="paymentPlan.minimum_amount"
                :placeholder="_('Enter minimum amount')"
              />
              <p v-if="minimumAmountError" class="help-block">{{ minimumAmountError }}</p>
              <p v-else class="help-block">{{ _('If the calculated advance payment falls below this amount, the full price is charged immediately.') }}</p>
            </div>

            <div class="form-group">
              <label>{{ _('Final payment timing') }}</label>
              <select class="form-control" v-model="paymentPlan.final_payment_type">
                <option value="fixed">{{ _('Fixed date') }}</option>
                <option value="offset">{{ _('Days before reservation start') }}</option>
              </select>
            </div>

            <div class="form-group" v-if="paymentPlan.final_payment_type === 'fixed'">
              <label for="paymentPlanDate">{{ _('Final payment date') }}</label>
              <DatePicker v-model="finalPaymentDate" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
            </div>

            <div class="form-group" v-if="paymentPlan.final_payment_type === 'offset'">
              <label for="paymentPlanOffset">{{ _('Days before reservation start') }}</label>
              <input
                type="number"
                step="1"
                min="0"
                class="form-control"
                id="paymentPlanOffset"
                v-model.number="paymentPlan.final_payment_offset"
                :placeholder="_('Enter number of days')"
              />
            </div>
          </div>

          <div v-else-if="paymentPlan.type === 'recurring'">
            <p class="help-block">{{ _('Products with this plan are sold as subscriptions. The price of the product is the price of one billing period, and each period is charged in advance.') }}</p>

            <h3>{{ _('Billing period') }}</h3>
            <div class="form-group">
              <label for="paymentPlanInterval">{{ _('Period length') }}</label>
              <div class="flex-row" style="gap: 10px; flex-wrap: nowrap;">
                <input type="number" step="1" min="1" class="form-control" style="width: 90px; flex: 0 0 90px;" id="paymentPlanIntervalCount" v-model.number="paymentPlan.interval_count" :aria-label="_('Number of periods')" />
                <select class="form-control" style="width: auto; flex: 1 1 auto;" id="paymentPlanInterval" v-model="paymentPlan.interval">
                  <option value="day">{{ intervalCountIsOne ? _('period|Day') : _('period|Days') }}</option>
                  <option value="week">{{ intervalCountIsOne ? _('Week') : _('Weeks') }}</option>
                  <option value="month">{{ intervalCountIsOne ? _('Month') : _('Months') }}</option>
                  <option value="year">{{ intervalCountIsOne ? _('Year') : _('Years') }}</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label for="paymentPlanAnchor">{{ _('Subscription starts') }}</label>
              <select class="form-control" id="paymentPlanAnchor" v-model="paymentPlan.anchor">
                <option value="purchase">{{ _('On the order date') }}</option>
                <option value="calendar">{{ _('At the start of the current month') }}</option>
                <option value="next_month">{{ _('At the start of the next month') }}</option>
                <option value="next_year">{{ _('At the start of the next year') }}</option>
                <option value="date">{{ _('On a set date') }}</option>
              </select>
            </div>

            <div class="form-group" v-if="paymentPlan.anchor === 'date'">
              <label>{{ _('Start date') }}</label>
              <DatePicker v-model="anchorDate" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
              <p class="help-block">{{ _('Billing periods are counted from this date. If the date has passed, the customer joins the ongoing period.') }}</p>
            </div>

            <div class="checkbox" v-if="paymentPlan.anchor === 'calendar' || paymentPlan.anchor === 'date'">
              <label>
                <input type="checkbox" v-model="prorateFirst" /> {{ _('Charge only the remaining part of the first period') }}
              </label>
            </div>

            <h3>{{ _('Term') }}</h3>
            <div class="form-group">
              <label for="paymentPlanTermType">{{ _('Duration') }}</label>
              <select class="form-control" id="paymentPlanTermType" v-model="paymentPlan.term_type">
                <option value="open">{{ _('Until cancelled') }}</option>
                <option value="fixed">{{ _('Fixed term') }}</option>
              </select>
            </div>

            <div v-if="paymentPlan.term_type === 'fixed'">
              <div class="radio">
                <label><input type="radio" v-model="termMode" value="periods" /> {{ _('Number of billing periods') }}</label>
              </div>
              <div class="radio">
                <label><input type="radio" v-model="termMode" value="date" /> {{ _('Until a set date') }}</label>
              </div>
              <div class="form-group" v-if="termMode === 'periods'">
                <input type="number" step="1" min="1" class="form-control" v-model.number="paymentPlan.term_periods" :aria-label="_('Number of billing periods')" />
              </div>
              <div class="form-group" v-else>
                <label>{{ _('End date') }}</label>
                <DatePicker v-model="termEndDate" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
                <p class="help-block">{{ _('The last period is shortened and priced to end on this date.') }}</p>
              </div>
            </div>

            <div v-else>
              <div class="form-group">
                <label for="paymentPlanContract">{{ _('Contract period (billing periods)') }}</label>
                <input type="number" step="1" min="1" class="form-control" id="paymentPlanContract" v-model.number="paymentPlan.contract_periods" />
                <p class="help-block">{{ _('The customer commits to this many billing periods at a time. 1 means there is no commitment.') }}</p>
              </div>
              <div class="form-group">
                <label for="paymentPlanCancellation">{{ _('A cancellation takes effect') }}</label>
                <select class="form-control" id="paymentPlanCancellation" v-model="paymentPlan.cancellation">
                  <option value="contract_end">{{ _('At the end of the current contract period') }}</option>
                  <option value="next_contract_end">{{ _('At the end of the next contract period') }}</option>
                </select>
              </div>
            </div>

            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="customerCanCancel" /> {{ _('The customer can cancel the subscription on the order page') }}
              </label>
            </div>

            <h3>{{ _('Renewals') }}</h3>
            <div class="form-group">
              <label for="paymentPlanRenewalPayment">{{ _('The next periods are charged with') }}</label>
              <select class="form-control" id="paymentPlanRenewalPayment" v-model="paymentPlan.renewal_payment">
                <option value="link">{{ _('A payment link sent by email') }}</option>
                <option value="bill">{{ _('An invoice') }}</option>
              </select>
              <p class="help-block" v-if="paymentPlan.renewal_payment === 'link'">{{ _('If the customer pays the subscription with Vipps MobilePay or Stripe, the next periods are charged automatically when each period starts, without a payment link.') }}</p>
            </div>
            <div class="form-group" v-if="paymentPlan.renewal_payment === 'link'">
              <label for="paymentPlanGraceDays">{{ _('Days to pay before the subscription is suspended') }}</label>
              <input type="number" step="1" min="0" class="form-control" id="paymentPlanGraceDays" v-model.number="paymentPlan.grace_days" />
              <p class="help-block">{{ _('While suspended, the customer loses access that the subscription gives. Paying resumes the subscription.') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-on:click="remove" type="button" :disabled="!isRemoveButtonEnabled"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>
<style scoped>
.editor-body {
  margin-bottom: 15px;
}
</style>
<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import components from '~/lib/components'
import settingshelpers from '~/lib/settingshelpers'

export default {
  data () {
    return {
      paymentPlan: {
        type: 'advance',
        amount_type: 'relative',
        final_payment_type: 'offset'
      },
      termMode: 'periods'
    }
  },
  mixins: [components, settingshelpers],
  watch: {
    'paymentPlan.type' (type) {
      if (type === 'recurring') {
        this.applyRecurringDefaults()
      }
    }
  },
  computed: {
    intervalCountIsOne () {
      return !this.paymentPlan || parseInt(this.paymentPlan.interval_count || 1) === 1
    },
    anchorDate: {
      get () {
        return this.paymentPlan && this.paymentPlan.anchor_date ? DateTime.fromISO(this.paymentPlan.anchor_date).toJSDate() : null
      },
      set (newValue) {
        this.paymentPlan.anchor_date = newValue ? DateTime.fromJSDate(newValue).toISODate() : null
      }
    },
    termEndDate: {
      get () {
        return this.paymentPlan && this.paymentPlan.term_end_date ? DateTime.fromISO(this.paymentPlan.term_end_date).toJSDate() : null
      },
      set (newValue) {
        this.paymentPlan.term_end_date = newValue ? DateTime.fromJSDate(newValue).toISODate() : null
      }
    },
    prorateFirst: {
      get () {
        return !!parseInt(this.paymentPlan.prorate_first, 10)
      },
      set (value) {
        this.paymentPlan.prorate_first = value ? 1 : 0
      }
    },
    customerCanCancel: {
      get () {
        return !!parseInt(this.paymentPlan.customer_can_cancel, 10)
      },
      set (value) {
        this.paymentPlan.customer_can_cancel = value ? 1 : 0
      }
    },
    remainingPercentage () {
      const amount = parseFloat(this.paymentPlan.amount) || 0
      return Math.max(0, 100 - amount)
    },
    finalPaymentDate: {
      get () {
        if (this.paymentPlan && this.paymentPlan.final_payment_date) {
          return DateTime.fromISO(this.paymentPlan.final_payment_date).toJSDate()
        }
        return null
      },
      set (newValue) {
        this.paymentPlan.final_payment_date = newValue ? DateTime.fromJSDate(newValue).toISODate() : null
      }
    },
    amountError () {
      if (this.paymentPlan.type !== 'advance') return null
      const amount = this.paymentPlan.amount
      if (amount === null || amount === undefined || amount === '') return null
      if (amount < 0) return this.paymentPlan.amount_type === 'relative'
        ? this._('Initial payment percentage must be greater than 0.')
        : this._('Must be greater than 0.')
      if (this.paymentPlan.amount_type === 'relative' && amount > 100) return this._('Initial payment percentage cannot exceed 100%.')
      return null
    },
    minimumAmountError () {
      const amount = this.paymentPlan.minimum_amount
      if (amount === null || amount === undefined || amount === '') return null
      if (amount <= 0) return this._('Must be greater than 0.')
      return null
    },
    isSaveButtonEnabled () {
      if (this.saving || this.saved || this.loading || this.locked) return false
      if (!this.paymentPlan.name || !this.paymentPlan.type) return false
      if (this.paymentPlan.type === 'advance') {
        if (!this.paymentPlan.amount && this.paymentPlan.amount !== 0) return false
        if (this.amountError || this.minimumAmountError) return false
        if (this.paymentPlan.final_payment_type === 'offset') {
          if (!this.paymentPlan.final_payment_offset && this.paymentPlan.final_payment_offset !== 0) return false
        } else if (this.paymentPlan.final_payment_type === 'fixed') {
          if (!this.paymentPlan.final_payment_date) return false
        }
      } else if (this.paymentPlan.type === 'recurring') {
        if (!this.paymentPlan.interval || !(parseInt(this.paymentPlan.interval_count, 10) >= 1)) return false
        if (this.paymentPlan.anchor === 'date' && !this.paymentPlan.anchor_date) return false
        if (this.paymentPlan.term_type === 'fixed') {
          if (this.termMode === 'periods' && !(parseInt(this.paymentPlan.term_periods, 10) >= 1)) return false
          if (this.termMode === 'date' && !this.paymentPlan.term_end_date) return false
        }
      }
      return true
    },
    isRemoveButtonEnabled () {
      return !this.saving && !this.saved && !this.loading && !this.locked && !!this.paymentPlan.id
    }
  },
  methods: {
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'type',
        'amount_type',
        'amount',
        'minimum_amount',
        'final_payment_type',
        'final_payment_date',
        'final_payment_offset',
        'interval',
        'interval_count',
        'anchor',
        'anchor_date',
        'prorate_first',
        'contract_periods',
        'cancellation',
        'term_type',
        'term_periods',
        'term_end_date',
        'renewal_payment',
        'grace_days',
        'customer_can_cancel'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.paymentPlan[e], a), {})
      if (data.type === 'recurring') {
        data.amount_type = data.amount = data.minimum_amount = data.final_payment_type = data.final_payment_date = data.final_payment_offset = null
        if (data.anchor !== 'date') {
          data.anchor_date = null
        }
        if (data.anchor !== 'calendar' && data.anchor !== 'date') {
          data.prorate_first = 0
        }
        if (data.term_type === 'fixed') {
          data.contract_periods = 1
          if (this.termMode === 'periods') {
            data.term_end_date = null
          } else {
            data.term_periods = 0
          }
        } else {
          data.term_periods = 0
          data.term_end_date = null
        }
        return data
      }
      // Clear fields that don't apply to the current selections
      if (data.amount_type === 'fixed') {
        data.minimum_amount = null
      }
      if (data.final_payment_type === 'offset') {
        data.final_payment_date = null
      } else if (data.final_payment_type === 'fixed') {
        data.final_payment_offset = null
      }
      return data
    },
    applyRecurringDefaults () {
      const defaults = {
        interval: 'month',
        interval_count: 1,
        anchor: 'purchase',
        prorate_first: 0,
        contract_periods: 1,
        cancellation: 'contract_end',
        term_type: 'open',
        renewal_payment: 'link',
        grace_days: 7,
        customer_can_cancel: 1
      }
      for (const key in defaults) {
        if (this.paymentPlan[key] === undefined || this.paymentPlan[key] === null || this.paymentPlan[key] === '') {
          this.paymentPlan[key] = defaults[key]
        }
      }
      this.termMode = parseInt(this.paymentPlan.term_periods, 10) > 0 || !this.paymentPlan.term_end_date ? 'periods' : 'date'
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/paymentplans/' + this.paymentPlan.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.deleted = false
        return this.showError(this._('Deletion failed!'))
      }
      this.close()
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/paymentplans/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/paymentplans.json?apikey=' + this.apiKey, data)
        }
      } catch (e) {
        this.saving = false
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadPaymentPlan(response.data.id)
      }
      this.saving = false
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    loadPaymentPlan (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/paymentplans/' + id + '.json?apikey=' + this.apiKey)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        this.paymentPlan = response.data
        if (this.paymentPlan.type === 'recurring') {
          this.applyRecurringDefaults()
        }
        this.storeState()
        this.loading = false
        resolve(this.paymentPlan)
      })
    }
  },
  created () {
    if (this.component && this.component.initData && this.component.initData.paymentPlanId) {
      this.loadPaymentPlan(this.component.initData.paymentPlanId)
    } else if (this.component && this.component.initData && this.component.initData.planType) {
      this.paymentPlan.type = this.component.initData.planType
    }
  },
  mounted () {
    if (!this.events) {
      let events = {}
      events['paymentplanUpdate'] = async (message) => {
        if (message && message.data && this.paymentPlan && parseInt(message.data.id, 10) === parseInt(this.paymentPlan.id, 10)) {
          if (!this.unsavedChanges) {
            this.loadPaymentPlan(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.paymentPlan && this.paymentPlan.id && parseInt(this.paymentPlan.id, 10) !== parseInt(this.component.initData.paymentPlanId, 10)) {
        try {
          await this.loadPaymentPlan(this.component.initData.paymentPlanId)
        } catch (e) {}
      }
    }
  }
}
</script>
